import { Link } from "@tanstack/react-router";
import { ArrowRight, ChevronDown } from "lucide-react";
import { useState } from "react";
import { Reveal } from "@/components/reveal";
import { buildFaqJsonLd, JsonLd } from "@/lib/json-ld";

export type FaqItemData = {
	question: string;
	answer: string;
};

const DEFAULT_FAQS: FaqItemData[] = [
	{
		question: "Is AgentFramer an image generator?",
		answer:
			"No. AgentFramer is the generation execution layer for AI agents — an MCP-native interface for image, video, and audio generation. It's not a prompt playground or design tool; the dashboard exists to manage workspaces, balance, and generation history.",
	},
	{
		question: "Do I need provider API keys?",
		answer:
			"No. AgentFramer ships with managed provider credentials, asset storage, and delivery built in. One MCP setup replaces wiring up separate image, video, and audio APIs per tool.",
	},
	{
		question: "Which agent clients work with it?",
		answer:
			"Claude, Cursor, Windsurf, Codex, and any MCP-compatible client. Run `npx agentframer@latest init`, complete browser auth, restart your AI session, and your agent can call AgentFramer tools.",
	},
	{
		question: "How are async jobs handled?",
		answer:
			"Prompt, dispatch, poll, retrieve. Your agent calls a tool, AgentFramer dispatches to the right provider, the agent polls for completion, and the output URL is returned and stored in your generation history for later retrieval.",
	},
	{
		question: "How does pricing work?",
		answer:
			"AgentFramer is prepaid and usage-based. Top up a workspace balance via Stripe before generation; image, video, and audio jobs deduct from it as your agent creates. No subscription, no seat fee, no monthly minimum.",
	},
	{
		question: "How fast is setup?",
		answer:
			"Under five minutes. Run `npx agentframer@latest init`, authenticate in the browser, restart your AI session, and your agent has `generate_image`, `generate_video`, and `generate_audio` available.",
	},
];

export function FaqSection({
	items = DEFAULT_FAQS,
}: {
	items?: FaqItemData[];
}) {
	const [openIndex, setOpenIndex] = useState(0);

	return (
		<section className="bg-border">
			<JsonLd data={buildFaqJsonLd(items)} />
			<div className="grid grid-cols-1 gap-px lg:grid-cols-3">
				<div className="homepage-hover-cell rounded-md bg-background p-8 sm:p-12">
					<Reveal>
						<h2 className="text-3xl font-semibold sm:text-4xl">
							Frequently
							<br />
							Asked Questions
						</h2>
						<p className="mt-6 max-w-xs text-base text-muted-foreground">
							What AI builders usually ask before wiring MCP-native media
							generation into an agent workflow.
						</p>
						<Link
							to="/help/quick-start"
							className="mt-6 inline-flex items-center gap-2 rounded-md bg-brand-accent px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-accent/90"
						>
							Read the quickstart
							<ArrowRight className="size-3.5" />
						</Link>
					</Reveal>
				</div>

				<div className="homepage-hover-cell rounded-md bg-background p-4 sm:p-6 lg:col-span-2 lg:p-8">
					<Reveal delay={120}>
						<ul className="space-y-2">
							{items.map((item, i) => {
								const open = i === openIndex;
								return (
									<li key={item.question}>
										<div
											className={`rounded-xl bg-card transition-colors ${
												open ? "ring-1 ring-border" : ""
											}`}
										>
											<button
												type="button"
												onClick={() => setOpenIndex(open ? -1 : i)}
												aria-expanded={open}
												className="flex w-full items-start justify-between gap-4 rounded-xl px-5 py-4 text-left"
											>
												<span className="text-base font-medium text-foreground">
													{item.question}
												</span>
												<ChevronDown
													className={`mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform ${
														open ? "rotate-180" : ""
													}`}
												/>
											</button>
											{open && (
												<div className="px-5 pb-4">
													<p className="text-sm leading-relaxed text-muted-foreground">
														{item.answer}
													</p>
												</div>
											)}
										</div>
										{!open && i < items.length - 1 && (
											<div className="mx-5 border-b border-border/60" />
										)}
									</li>
								);
							})}
						</ul>
					</Reveal>
				</div>
			</div>
		</section>
	);
}
